import { type ComponentRef, useRef, useState } from 'react';
import Select, { type MultiValue } from 'react-select';
import { useDocumentClick } from '../../../hooks/document';
import { getSharedStyles } from '../../../utils/select';
import translations from '../../../translations';
import { ExIconButton, IconButtonType } from '@boomi/exosphere';
import './css/home.less';

interface FlowFiltersPopupProps {
    allFlowLabels: string[];
    filterLabels: string[];
    filterIsPublic: boolean | null;
    onFilterLabelsChange: (options: MultiValue<{ value: string; label: string }>) => void;
    onFilterIsPublicChange: (isPublic: boolean | null) => void;
}

const FlowFiltersPopup = ({
    allFlowLabels,
    filterLabels,
    filterIsPublic,
    onFilterLabelsChange,
    onFilterIsPublicChange,
}: FlowFiltersPopupProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropDownRef = useRef<ComponentRef<'div'>>(null);

    useDocumentClick(dropDownRef, () => setIsOpen(false), true);

    const handleToggleClick = () => {
        setIsOpen(!isOpen);
    };

    return (
        <div ref={dropDownRef} className="flow-filters-wrapper">
            <ExIconButton
                icon="Filter simple"
                type={IconButtonType.SECONDARY}
                label={translations.VALUE_SELECTOR_show_filters}
                indicator={Number(filterLabels.length > 0) + Number(filterIsPublic !== null)}
                onClick={handleToggleClick}
                data-testid="filter-button"
            />
            {isOpen && (
                <div className="flow-filters-content" data-testid="flow-filters-content">
                    <Select
                        inputId="flow-label-filter"
                        options={allFlowLabels.map((label) => ({
                            value: label,
                            label,
                        }))}
                        value={filterLabels.map((label) => ({
                            value: label,
                            label,
                        }))}
                        onChange={onFilterLabelsChange}
                        isMulti={true}
                        styles={getSharedStyles<{ label: string; value: string }, true>()}
                        placeholder={translations.HOME_flow_filter_placeholder}
                        noOptionsMessage={() => translations.HOME_flow_filter_none}
                    />
                    <Select
                        inputId="flow-access-filter"
                        options={[
                            {
                                value: 'true',
                                label: translations.FLOW_auth_public_label,
                            },
                            {
                                value: 'false',
                                label: translations.FLOW_auth_secured_label,
                            },
                        ]}
                        value={
                            filterIsPublic !== null
                                ? {
                                      value: filterIsPublic.toString(),
                                      label: filterIsPublic
                                          ? translations.FLOW_auth_public_label
                                          : translations.FLOW_auth_secured_label,
                                  }
                                : null
                        }
                        onChange={(selectedOption) => {
                            onFilterIsPublicChange(
                                selectedOption ? selectedOption.value === 'true' : null,
                            );
                        }}
                        styles={getSharedStyles<{ label: string; value: string }, false>()}
                        placeholder={translations.HOME_flow_access_filter_placeholder}
                        isSearchable={false}
                        isClearable={true}
                    />
                </div>
            )}
        </div>
    );
};

export default FlowFiltersPopup;
